import { computed, ref, watch } from 'vue';
import { useAvailableOptions } from './useAvailableOptions';
import {
  TechDataVehicle,
  CabinTypeLabel,
  RoofTypeLabel,
  DrivetrainLabel,
  EngineTypeLabel,
  CabinVariations,
  RoofVariations,
  SizeEngineVariations,
  SizeVariations,
  TechDataVariations,
} from '@/types';
type TransmissionMode = 'manual' | 'automatic';

export function useVehicleSelection(
  vehicleType: string,
  variations: TechDataVariations,
  vehicles: TechDataVehicle[],
  selectedVariant?: TechDataVehicle,
) {
  const vehicleWeightMaxValue = (e: TechDataVehicle): number | null => {
    // prefer flattened
    if (
      e.weights &&
      (e.weights as any).weightMax &&
      (e.weights as any).weightMax.value !== undefined
    ) {
      return (e.weights as any).weightMax.value;
    }

    // per-transmission
    if (
      e.weights &&
      (e.weights as any).manual &&
      (e.weights as any).manual.weightMax &&
      (e.weights as any).manual.weightMax.value !== undefined
    ) {
      return (e.weights as any).manual.weightMax.value;
    }
    if (
      e.weights &&
      (e.weights as any).automatic &&
      (e.weights as any).automatic.weightMax &&
      (e.weights as any).automatic.weightMax.value !== undefined
    ) {
      return (e.weights as any).automatic.weightMax.value;
    }

    return null;
  };

  // State for selected properties
  const selectedRoof = ref<RoofTypeLabel | null>(
    selectedVariant?.roofHeightType || null,
  );
  const selectedCabin = ref<CabinTypeLabel | null>(
    selectedVariant?.cabinType || null,
  );
  const normalize = (t: unknown): TransmissionMode | null => {
    const v = String(t ?? '').toLowerCase();
    return v === 'manual' || v === 'automatic' ? (v as TransmissionMode) : null;
  };

  const selectedTransmission = ref<TransmissionMode | null>(
    normalize(selectedVariant?.transmissions?.[0]) ?? null,
  );
  const selectedEngine = ref<EngineTypeLabel | null>(
    selectedVariant?.engineType || null,
  );
  const selectedBatteryCapacity = ref<number | null>(
    selectedVariant?.batteryCapacity || null,
  );
  const selectedWeight = ref<number | null>(
    selectedVariant ? vehicleWeightMaxValue(selectedVariant) : null,
  );
  const selectedDrivetrain = ref<DrivetrainLabel | null>(
    selectedVariant?.drivetrainType || null,
  );

  // Current variation and vehicle
  const variation = ref<
    CabinVariations | RoofVariations | SizeEngineVariations | SizeVariations
  >(variations.variations[0]);

  const vehicle = ref<TechDataVehicle | undefined>(selectedVariant);

  // Use useAvailableOptions to get available options
  const {
    available_weights,
    available_cabins,
    available_roofs,
    available_engines,
    available_batteryCapacities,
    available_drivetrains,
  } = useAvailableOptions(
    vehicleType,
    variation,
    selectedRoof,
    selectedCabin,
    selectedWeight,
    selectedEngine,
    selectedBatteryCapacity,
  );

  // Initialize default values if no selectedVariant is provided
  if (!selectedVariant) {
    const setDefaultValues = () => {
      const firstVariation = variations.variations[0];

      // Default values object
      const defaults = {
        roof: null as RoofTypeLabel | null,
        cabin: null as CabinTypeLabel | null,
        engine: null as EngineTypeLabel | null,
        batteryCapacity: null as number | null,
        weight: null as number | null,
        drivetrain: null as DrivetrainLabel | null,
        transmission: null as TransmissionMode | null,
      };

      if (vehicleType === 'Sprinter_Roof') {
        const roofVariation = firstVariation as RoofVariations;
        const roofHeightType = roofVariation.roofHeightTypes[0];
        if (roofHeightType?.weightsMax?.length) {
          const weightMax = roofHeightType.weightsMax[0];
          if (weightMax.drivetrainTypes?.length) {
            const drivetrainType = weightMax.drivetrainTypes[0];
            if (drivetrainType.engineTypes?.length) {
              const engineType = drivetrainType.engineTypes[0];

              defaults.roof = roofHeightType.roofHeightType;
              defaults.engine = engineType.engineType;
              defaults.batteryCapacity =
                engineType.batteryCapacities?.[0] || null;
              defaults.weight = weightMax.weightMax;
              defaults.drivetrain = drivetrainType.drivetrainType;
            }
          }
        }
      } else if (vehicleType === 'Sprinter_Cabin') {
        const cabinVariation = firstVariation as CabinVariations;
        if (cabinVariation.cabinTypes?.length) {
          const cabinType = cabinVariation.cabinTypes[0];
          if (cabinType.weightsMax?.length) {
            const weightMax = cabinType.weightsMax[0];
            if (weightMax.drivetrainTypes?.length) {
              const drivetrainType = weightMax.drivetrainTypes[0];
              if (drivetrainType.engineTypes?.length) {
                const engineType = drivetrainType.engineTypes[0];

                defaults.cabin = cabinType.cabinType;
                defaults.engine = engineType.engineType;
                defaults.batteryCapacity =
                  engineType.batteryCapacities?.[0] || null;
                defaults.weight = weightMax.weightMax;
                defaults.drivetrain = drivetrainType.drivetrainType;
              }
            }
          }
        }
      } else if (vehicleType === 'Citan' || vehicleType === 'Vito') {
        // Check if variation is SizeEngineVariations for engineTypes
        if (
          'engineTypes' in firstVariation &&
          firstVariation.engineTypes?.length
        ) {
          const sizeEngineVariation = firstVariation as SizeEngineVariations;
          defaults.engine = sizeEngineVariation.engineTypes[0].engineType;
          defaults.batteryCapacity =
            sizeEngineVariation.engineTypes[0].batteryCapacities?.[0] || null;
        }

        // Check if variation is SizeVariations for weightsMax
        if (
          'weightsMax' in firstVariation &&
          firstVariation.weightsMax?.length
        ) {
          const sizeVariation = firstVariation as SizeVariations;
          defaults.weight = sizeVariation.weightsMax[0].weightMax;

          if (sizeVariation.weightsMax[0].drivetrainTypes?.length) {
            defaults.drivetrain =
              sizeVariation.weightsMax[0].drivetrainTypes[0].drivetrainType;
          }
        }
      }

      return defaults;
    };

    // Apply default values
    const defaults = setDefaultValues();
    selectedRoof.value = defaults.roof;
    selectedCabin.value = defaults.cabin;
    selectedEngine.value = defaults.engine;
    selectedBatteryCapacity.value = defaults.batteryCapacity;
    selectedWeight.value = defaults.weight;
    selectedDrivetrain.value = defaults.drivetrain;
    selectedTransmission.value = defaults.transmission;
  }

  // Helper functions to check if property needs to be changed
  const shouldRoofChange = () =>
    selectedRoof.value && !available_roofs.value.includes(selectedRoof.value);

  const shouldCabinChange = () =>
    selectedCabin.value &&
    !available_cabins.value.includes(selectedCabin.value);

  const shouldWeightMaxChange = () =>
    selectedWeight.value &&
    !available_weights.value.includes(selectedWeight.value);

  const shouldChangeDrivetrain = () =>
    selectedDrivetrain.value &&
    !available_drivetrains.value.includes(selectedDrivetrain.value);

  const shouldEngineChange = () =>
    selectedEngine.value &&
    !available_engines.value.includes(selectedEngine.value);

  const shouldBatteryCapacityChange = () =>
    selectedBatteryCapacity.value &&
    !available_batteryCapacities.value.includes(selectedBatteryCapacity.value);

  // Change properties based on selection
  const changeProperties = (
    label: CabinTypeLabel | RoofTypeLabel | EngineTypeLabel | number | null,
  ) => {
    if (!label) return;

    if (shouldWeightMaxChange() && available_weights.value.length) {
      selectedWeight.value = available_weights.value[0];
    }

    if (shouldChangeDrivetrain() && available_drivetrains.value.length) {
      selectedDrivetrain.value = available_drivetrains.value[0];
    }

    if (shouldEngineChange() && available_engines.value.length) {
      selectedEngine.value = available_engines.value[0];
    }

    if (
      shouldBatteryCapacityChange() &&
      available_batteryCapacities.value.length
    ) {
      selectedBatteryCapacity.value = available_batteryCapacities.value[0];
    }

    selectVehicle();
  };

  // Set vehicle properties from a selected variation
  const setVehiclePropertiesBySize = (
    selectedVariation:
      | CabinVariations
      | RoofVariations
      | SizeEngineVariations
      | SizeVariations,
  ) => {
    // Set the new variation
    variation.value = selectedVariation;

    switch (vehicleType) {
      case 'Citan':
        // Update engine and battery capacity for Citan
        if (
          'engineTypes' in selectedVariation &&
          selectedVariation.engineTypes?.length
        ) {
          if (shouldEngineChange()) {
            selectEngine(selectedVariation.engineTypes[0].engineType);
          }
          if (shouldBatteryCapacityChange()) {
            selectBatteryCapacity(
              selectedVariation.engineTypes[0].batteryCapacities?.[0] || null,
            );
          }
        }
        break;
      case 'Vito':
        if (
          'weightsMax' in selectedVariation &&
          selectedVariation.weightsMax?.length
        ) {
          if (shouldWeightMaxChange() && available_weights.value.length) {
            selectWeight(available_weights.value[0]);
          }

          if (shouldChangeDrivetrain() && available_drivetrains.value.length) {
            selectDrivetrain(available_drivetrains.value[0]);
          }

          if (shouldEngineChange() && available_engines.value.length) {
            selectEngine(available_engines.value[0]);
          }

          if (
            shouldBatteryCapacityChange() &&
            available_batteryCapacities.value.length
          ) {
            selectBatteryCapacity(available_batteryCapacities.value[0]);
          }
        }
        break;
      case 'Sprinter_Cabin':
        if (
          shouldCabinChange() &&
          (selectedVariation as CabinVariations).cabinTypes?.length
        ) {
          selectCabin(
            (selectedVariation as CabinVariations).cabinTypes[0].cabinType,
          );
        }

        if ((selectedVariation as CabinVariations).cabinTypes?.length) {
          const cabinType = (selectedVariation as CabinVariations)
            .cabinTypes[0];
          if (cabinType.weightsMax?.length) {
            const weightObj = cabinType.weightsMax[0];
            if (weightObj.drivetrainTypes?.length) {
              const drivetrainType = weightObj.drivetrainTypes[0];
              if (drivetrainType.engineTypes?.length) {
                selectEngine(drivetrainType.engineTypes[0].engineType);
              }
            }
          }
        }
        break;
      case 'Sprinter_Roof': {
        const rv = selectedVariation as RoofVariations;

        if (!rv.roofHeightTypes.length) break;

        const nextRoof = rv.roofHeightTypes[0].roofHeightType;
        selectedRoof.value = nextRoof;

        selectedWeight.value = null;
        selectedDrivetrain.value = null;
        selectedEngine.value = null;
        selectedBatteryCapacity.value = null;

        const weights = available_weights.value;
        if (weights.length) {
          selectedWeight.value = weights[0];
        }

        if (available_drivetrains.value.length) {
          selectedDrivetrain.value = available_drivetrains.value[0];
        }

        if (available_engines.value.length) {
          selectedEngine.value = available_engines.value[0];
        }

        if (available_batteryCapacities.value.length) {
          selectedBatteryCapacity.value = available_batteryCapacities.value[0];
        }

        selectVehicle();
        break;
      }
    }
    // Ensure the vehicle is updated after changing the variation
    selectVehicle();
  };

  const normalizeTransmission = (t: unknown): TransmissionMode | null => {
    const v = String(t ?? '').toLowerCase();
    return v === 'manual' || v === 'automatic' ? (v as TransmissionMode) : null;
  };

  // Select final vehicle based on properties
  const selectVehicle = () => {
    const pool = matchingVehicles.value;
    if (!pool.length) {
      vehicle.value = undefined;
      return undefined;
    }

    const transmission = selectedTransmission.value;

    const found = transmission
      ? pool.find((e) =>
          (e.transmissions ?? []).some(
            (t) => normalizeTransmission(t) === transmission,
          ),
        )
      : pool[0];

    vehicle.value = found;
    return found;
  };

  // Selection functions
  const selectCabin = (cabin: CabinTypeLabel | null) => {
    selectedCabin.value = cabin;
    changeProperties(cabin);
    return selectVehicle();
  };

  const selectRoof = (roof: RoofTypeLabel | null) => {
    selectedRoof.value = roof;
    changeProperties(roof);
    return selectVehicle();
  };

  const selectEngine = (engine: EngineTypeLabel | null) => {
    selectedEngine.value = engine;
    changeProperties(engine);
    return selectVehicle();
  };

  const selectBatteryCapacity = (batteryCapacity: number | null) => {
    selectedBatteryCapacity.value = batteryCapacity;
    changeProperties(batteryCapacity);

    if (vehicleType === 'Vito' && batteryCapacity != null) {
      const matchingVehicle = vehicles.find(
        (e) =>
          e.sizeType === variation.value.sizeType &&
          e.drivetrainType === selectedDrivetrain.value &&
          e.engineType === selectedEngine.value &&
          e.batteryCapacity === batteryCapacity,
      );

      if (matchingVehicle) {
        const newWeight = vehicleWeightMaxValue(matchingVehicle);

        if (newWeight !== selectedWeight.value) {
          selectedWeight.value = newWeight;
        }

        vehicle.value = matchingVehicle;
        return matchingVehicle;
      }
    }

    return selectVehicle();
  };

  const selectWeight = (weight: number | null) => {
    selectedWeight.value = weight;
    if (shouldChangeDrivetrain()) {
      selectDrivetrain(available_drivetrains.value[0]);
    }
    return selectVehicle();
  };

  const selectDrivetrain = (drivetrain: DrivetrainLabel | null) => {
    selectedDrivetrain.value = drivetrain;
    return selectVehicle();
  };

  const selectTransmission = (transmission: TransmissionMode | null) => {
    if (transmission && !available_transmissions.value.includes(transmission)) {
      selectedTransmission.value = available_transmissions.value[0] ?? null;
    } else {
      selectedTransmission.value = transmission;
    }

    return selectVehicle();
  };

  const matchesWithoutTransmission = (e: TechDataVehicle) => {
    if (vehicleType === 'Citan') {
      return (
        e.sizeType === variation.value.sizeType &&
        e.engineType === selectedEngine.value &&
        (selectedBatteryCapacity.value == null ||
          e.batteryCapacity === selectedBatteryCapacity.value)
      );
    }

    if (vehicleType === 'Vito') {
      return (
        e.sizeType === variation.value.sizeType &&
        vehicleWeightMaxValue(e) === selectedWeight.value &&
        e.drivetrainType === selectedDrivetrain.value &&
        e.engineType === selectedEngine.value &&
        (selectedBatteryCapacity.value == null ||
          e.batteryCapacity === selectedBatteryCapacity.value)
      );
    }

    return (
      e.sizeType === variation.value.sizeType &&
      vehicleWeightMaxValue(e) === selectedWeight.value &&
      e.drivetrainType === selectedDrivetrain.value &&
      e.cabinType === selectedCabin.value &&
      e.roofHeightType === selectedRoof.value &&
      (selectedBatteryCapacity.value == null ||
        e.batteryCapacity === selectedBatteryCapacity.value)
    );
  };

  const matchingVehicles = computed(() =>
    vehicles.filter(matchesWithoutTransmission),
  );

  const available_transmissions = computed<TransmissionMode[]>(() => {
    const set = new Set<TransmissionMode>();

    for (const v of matchingVehicles.value) {
      for (const t of v.transmissions ?? []) {
        const tt = String(t).toLowerCase() as TransmissionMode;
        if (tt === 'manual' || tt === 'automatic') set.add(tt);
      }
    }

    return Array.from(set).sort((a, b) => {
      const rank: Record<TransmissionMode, number> = {
        manual: 0,
        automatic: 1,
      };
      return rank[a] - rank[b];
    });
  });

  watch(
    available_transmissions,
    (list) => {
      if (!list.length) {
        selectedTransmission.value = null;
        vehicle.value = undefined;
        return;
      }

      if (!selectedTransmission.value) {
        selectedTransmission.value = list[0];
      } else if (!list.includes(selectedTransmission.value)) {
        selectedTransmission.value = list[0];
      }

      selectVehicle();
    },
    { immediate: true },
  );

  return {
    // State
    selectedRoof,
    selectedCabin,
    selectedEngine,
    selectedBatteryCapacity,
    selectedWeight,
    selectedDrivetrain,
    selectedTransmission,
    variation,
    vehicle,

    // Available options
    available_weights,
    available_cabins,
    available_roofs,
    available_engines,
    available_batteryCapacities,
    available_drivetrains,
    available_transmissions,

    // Selection functions
    selectCabin,
    selectRoof,
    selectEngine,
    selectBatteryCapacity,
    selectWeight,
    selectDrivetrain,
    selectTransmission,
    selectVehicle,
    setVehiclePropertiesBySize,

    // Helper functions
    shouldRoofChange,
    shouldCabinChange,
    shouldWeightMaxChange,
    shouldChangeDrivetrain,
    shouldEngineChange,
    shouldBatteryCapacityChange,
  };
}
